import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { renderToStaticMarkup } from "react-dom/server";
import ChassisSvg, { MAP_W, MAP_H } from "../components/map/ChassisSvg";
import { ShapesLayer } from "../components/draw/ShapesLayer";
import { svgToCanvas, loadImage, downloadDataUrl } from "./image";
import { pointCalc, anomalyCalc, axleCalc, diagonalCalc, deviationLabel, fmtSigned, fmtAbs, fmtTol, fmtCoord, fmtPct, SIT } from "./calc";
import { BRAND, CLASS_STYLE } from "./constants";
import { finalClass } from "./laudo";
import { fmtDay, visualList } from "./model";

const T = (s) => String(s ?? "").replace(/Δ/g, "d").replace(/[—–−]/g, "-").replace(/•/g, "·").replace(/→/g, "->");

export function mapCanvas(insp, view, theme = "light", scale = 2) {
  const markup = renderToStaticMarkup(<ChassisSvg insp={insp} view={view} theme={theme} fixedSize />);
  return svgToCanvas(markup, MAP_W, MAP_H[view], scale);
}

async function photoCanvas(photo) {
  const canvas = document.createElement("canvas");
  canvas.width = photo.width;
  canvas.height = photo.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(await loadImage(photo.image), 0, 0, photo.width, photo.height);
  if ((photo.annotations || []).length) {
    const markup = renderToStaticMarkup(
      <svg xmlns="http://www.w3.org/2000/svg" width={photo.width} height={photo.height} viewBox={`0 0 ${photo.width} ${photo.height}`}>
        <ShapesLayer shapes={photo.annotations} halo="#000000" />
      </svg>,
    );
    ctx.drawImage(await svgToCanvas(markup, photo.width, photo.height, 1), 0, 0);
  }
  return canvas;
}

const fileBase = (insp) => `Laudo_${(insp.vehicle?.placa || "SEM_PLACA").replace(/\W/g, "")}_OS${(insp.vehicle?.numero_ordem_servico || "").replace(/\W/g, "") || "-"}`;
const tolOf = (v) => (v ? `±${v} mm` : SIT.SEM_TOL);

export async function exportPdf(insp, photos) {
  const { analysis, laudo } = insp;
  const v = insp.vehicle || {};
  const r = insp.reference || {};
  const sig = insp.signature || {};
  const final = finalClass(analysis);
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
  const W = 210, M = 14, TOP = 33, BOTTOM = 279;
  let y = TOP;
  const ensure = (h) => { if (y + h > BOTTOM) { doc.addPage(); y = TOP; } };
  const section = (n, title) => {
    ensure(16);
    y += 3;
    doc.setFillColor(255, 213, 0); doc.rect(M, y - 4.3, 1.8, 5.6, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(5, 11, 16);
    doc.text(T(`${n}. ${title.toUpperCase()}`), M + 4, y);
    doc.setDrawColor(5, 11, 16); doc.setLineWidth(0.3); doc.line(M, y + 1.6, W - M, y + 1.6);
    y += 7;
  };
  const para = (text, { size = 9, color = [40, 48, 60], bold = false } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal"); doc.setFontSize(size); doc.setTextColor(...color);
    doc.splitTextToSize(T(text), W - 2 * M).forEach((l) => { ensure(4.6); doc.text(l, M, y); y += 4.2; });
    y += 1.6;
  };
  const table = (head, body, redCols = [], redRows = []) => {
    autoTable(doc, {
      startY: y, head: [head.map(T)], body: body.map((row) => row.map(T)), theme: "grid",
      margin: { left: M, right: M, top: TOP, bottom: 297 - BOTTOM },
      styles: { fontSize: 7, cellPadding: 1.3, textColor: [30, 38, 50], lineColor: [205, 214, 225], lineWidth: 0.2 },
      headStyles: { fillColor: [5, 11, 16], textColor: [255, 213, 0], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [246, 248, 251] },
      didParseCell: (d) => {
        if (d.section !== "body") return;
        if (d.cell.raw === SIT.FORA || (redCols.includes(d.column.index) && (redRows.length === 0 || redRows.includes(d.row.index)))) {
          d.cell.styles.textColor = [255, 0, 0];
          d.cell.styles.fontStyle = "bold";
        }
      },
    });
    y = doc.lastAutoTable.finalY + 5;
  };
  const image = (canvas, wmm, x = M) => {
    const h = (wmm * canvas.height) / canvas.width;
    ensure(h + 2);
    doc.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", x, y, wmm, h);
    y += h + 3;
  };

  doc.setFont("helvetica", "bold"); doc.setFontSize(17); doc.setTextColor(5, 11, 16);
  doc.text(T("LAUDO TÉCNICO DE ALINHAMENTO DE CHASSIS"), M, y + 2);
  y += 9;
  const rgb = final ? CLASS_STYLE[final].rgb : [100, 116, 139];
  doc.setDrawColor(...rgb); doc.setLineWidth(0.8); doc.rect(M, y, W - 2 * M, 13);
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(100, 116, 139);
  doc.text("RESULTADO DO ALINHAMENTO", M + 4, y + 5);
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...rgb);
  doc.text(T(final || analysis.statusMsg), M + 4, y + 10.5);
  y += 19;

  section(1, "Identificação do veículo");
  table(["Campo", "Valor", "Campo", "Valor"], [
    ["Placa", v.placa || "—", "Cliente", v.cliente || "—"], ["Marca", v.marca || "—", "Modelo", v.modelo || "—"],
    ["Ano", v.ano || "—", "Cor", v.cor || "—"], ["Tipo de veículo", v.tipo_veiculo === "Outro" ? v.tipo_outro || "Outro" : v.tipo_veiculo || "—", "Nº OS", v.numero_ordem_servico || "—"],
    ["Data", fmtDay(v.data_inspecao), "Hora", v.hora_inspecao || "—"], ["Executor", v.executor || "—", "Empresa", BRAND.empresa],
  ]);
  section(2, "Objetivo da inspeção"); para(laudo.objetivo);
  section(3, "Metodologia de medição"); para(laudo.metodologia);
  section(4, "Pontos de referência utilizados"); para(laudo.pontos_referencia);
  table(["Parâmetro", "Valor"], [["Tolerância padrão (pontos)", tolOf(r.tolerancia_padrao)], ["Tolerância diagonais", tolOf(r.tolerancia_diagonal)], ["Tolerância eixos", tolOf(r.tolerancia_eixo)], ["Tolerância longarinas", tolOf(r.tolerancia_longarina)], ["Fonte das tolerâncias", analysis.fontes]]);

  const rows = (insp.points || []).map((p) => ({ p, c: pointCalc(p, r) }));
  section(5, "Tabela de medições");
  if (rows.length) table(["Nº", "Descrição", "Local", "Nominal", "Encontr.", "Dif.", "Result.", "Tol.", "Situação"], rows.map(({ p, c }) => [p.numero, p.descricao || "—", p.localizacao || "—", p.nominal || "—", p.encontrada || "—", fmtSigned(c.diff), fmtAbs(c.resultant), fmtTol(c.tol), c.situacao]));
  else para("Nenhum ponto de medição registrado.");
  const axles = (insp.axles || []).map((a) => ({ a, c: axleCalc(a, r) })).filter((o) => o.c.hasData);
  if (axles.length) table(["Eixo", "Posição X", "Esq.", "Centro", "Dir.", "Dif. E-D", "Desloc.", "Situação"], axles.map(({ a, c }) => [a.eixo, a.posicao_x || "—", a.esquerda || "—", a.centro || "—", a.direita || "—", fmtSigned(c.diffLR), fmtSigned(c.desl), c.hasData ? c.situacao : "—"]));
  const diag = diagonalCalc(insp.diagonals, r);
  if (diag.rows.length) table(["Diagonal", "De", "Até", "Medida", "Referência", "Dif.", "Situação"], diag.rows.map((d) => [d.nome, d.ponto_inicial || "—", d.ponto_final || "—", d.medida || "—", d.referencia || "—", fmtSigned(d.diff), d.diff !== null ? d.situacao : "—"]));
  if (diag.pairs.length) table(["Comparação", "Diferença entre diagonais", "Situação"], diag.pairs.map((p) => [`${p.a.nome} x ${p.b.nome}`, fmtSigned(p.cross), p.cross !== null ? p.situacao : "—"]));

  section(6, "Anomalias encontradas");
  if ((insp.anomalies || []).length) table(["Nº", "Tipo", "Localização", "dX", "dY", "dZ", "Total", "Tol.", "Sentido", "Gravidade"], insp.anomalies.map((a) => { const c = anomalyCalc(a); return [a.numero, a.tipo || "—", a.localizacao || "—", fmtSigned(c.dx), fmtSigned(c.dy), fmtSigned(c.dz), fmtAbs(c.total), tolOf(a.tolerancia), a.sentido || "—", a.gravidade || "—"]; }), [0]);
  else para("Nenhuma anomalia registrada no formulário.");
  const visualAnom = visualList(insp.visual).filter((x) => x.status === "ANOMALIA");
  if (visualAnom.length) para(`Inspeção visual: ${visualAnom.map((x) => `${x.item}${x.obs ? ` (${x.obs})` : ""}`).join("; ")}.`);

  section(7, "Representação gráfica dos desvios");
  image(await mapCanvas(insp, "top"), W - 2 * M);
  image(await mapCanvas(insp, "side"), W - 2 * M);

  section(8, "Coordenadas dos pontos");
  if (rows.length) {
    const foraIdx = rows.map((x, i) => (x.c.situacao === SIT.FORA ? i : -1)).filter((i) => i >= 0);
    table(["Nº", "Ref X", "Ref Y", "Ref Z", "Enc X", "Enc Y", "Enc Z", "dX", "dY", "dZ", "Identificação"], rows.map(({ p, c }) => [p.numero, fmtCoord(p.ref?.x), fmtCoord(p.ref?.y), fmtCoord(p.ref?.z), fmtCoord(p.found?.x), fmtCoord(p.found?.y), fmtCoord(p.found?.z), fmtSigned(c.dx), fmtSigned(c.dy), fmtSigned(c.dz), deviationLabel(p.numero, c).text]), [7, 8, 9], foraIdx.length ? foraIdx : [-1]);
  } else para("Sem coordenadas registradas.");

  section(9, "Análise das diferenças");
  para(`Pontos medidos: ${analysis.counts.medidos} · Fora da tolerância: ${analysis.counts.fora} · Atenção: ${analysis.counts.atencao} · Maior desvio: ${analysis.maior ? `${fmtAbs(analysis.maior.valor)} (${analysis.maior.ponto} - ${analysis.maior.local})` : "—"} · Desvio médio: ${fmtAbs(analysis.media)} · Conformidade: ${fmtPct(analysis.conformidade)} · Tipo principal de anomalia: ${analysis.tipoPrincipal}`, { bold: true });
  analysis.findings.forEach((f) => para(`- ${f.texto}`, { color: f.nivel === "fora" ? [255, 0, 0] : [40, 48, 60] }));
  analysis.warnings.forEach((w) => para(`! ${w}`, { color: [180, 83, 9] }));

  section(10, "Conclusão técnica"); para(laudo.conclusao);
  section(11, "Recomendações");
  (laudo.recomendacoes || "").split("\n").filter(Boolean).forEach((x) => para(`- ${x}`));
  if (laudo.observacoes) para(`Observações complementares: ${laudo.observacoes}`);

  section(12, "Registro fotográfico");
  if (!photos.length) para("Sem registro fotográfico.");
  const colW = (W - 2 * M - 6) / 2;
  for (let i = 0; i < photos.length; i += 2) {
    const pair = photos.slice(i, i + 2);
    const canvases = await Promise.all(pair.map(photoCanvas));
    const hs = canvases.map((c) => Math.min((colW * c.height) / c.width, 80));
    const rowH = Math.max(...hs) + 12;
    ensure(rowH);
    canvases.forEach((c, j) => {
      const x = M + j * (colW + 6);
      const w = Math.min(colW, (hs[j] * c.width) / c.height);
      doc.addImage(c.toDataURL("image/jpeg", 0.88), "JPEG", x, y, w, hs[j]);
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(5, 11, 16);
      const p = pair[j];
      doc.text(T(`Foto ${i + j + 1} · ${p.local || "—"}${p.anomalia ? ` · Anomalia ${p.anomalia}` : ""}`), x, y + hs[j] + 4);
      doc.setFont("helvetica", "normal"); doc.setTextColor(60, 70, 85);
      doc.text(doc.splitTextToSize(T(`${p.descricao || ""}${p.observacao ? ` - ${p.observacao}` : ""}`), colW).slice(0, 2), x, y + hs[j] + 7.5);
    });
    y += rowH;
  }

  section(13, "Assinatura do executor");
  ensure(40);
  if (sig.image) doc.addImage(sig.image, "PNG", M, y, 60, 19);
  y += 21;
  doc.setDrawColor(5, 11, 16); doc.setLineWidth(0.3); doc.line(M, y, M + 80, y);
  y += 4;
  para(`${sig.executor || v.executor || "—"}\nRegistro profissional: ${sig.registro_profissional || "—"}\n${BRAND.empresa} · Data: ${fmtDay(sig.data)}`);

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFillColor(5, 11, 16); doc.rect(0, 0, W, 22, "F");
    doc.setFillColor(255, 213, 0); doc.rect(0, 22, W, 1.3, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(255, 213, 0); doc.text("NILLO", M, 11);
    doc.setTextColor(255, 255, 255); doc.text("EQUIPAMENTOS", M + 17.5, 11);
    doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(148, 163, 184);
    doc.text(T(`${BRAND.slogan} · ${BRAND.telefone} · ${BRAND.atendimento}`), M, 16.5);
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(255, 255, 255);
    doc.text(T("RELATÓRIO DIGITAL DE ALINHAMENTO DE CHASSIS"), W - M, 10, { align: "right" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
    doc.text(T(`PLACA ${v.placa || "—"} · OS ${v.numero_ordem_servico || "—"} · ${fmtDay(v.data_inspecao)}`), W - M, 15.5, { align: "right" });
    doc.setDrawColor(255, 213, 0); doc.setLineWidth(0.6); doc.line(M, 283, W - M, 283);
    doc.setFontSize(6.3); doc.setTextColor(90, 100, 115);
    doc.text(T(`${BRAND.empresa} - ${BRAND.atividade}`), M, 287);
    doc.text(T(`WhatsApp ${BRAND.telefone} · ${BRAND.atendimentoRodape}`), M, 290.5);
    doc.setFont("helvetica", "bold"); doc.setTextColor(5, 11, 16);
    doc.text(T(BRAND.frase), M, 294);
    doc.text(`${i}/${pages}`, W - M, 290.5, { align: "right" });
  }
  doc.save(`${fileBase(insp)}.pdf`);
}

export async function exportSummaryImage(insp, kind = "png") {
  const { analysis } = insp;
  const v = insp.vehicle || {};
  const final = finalClass(analysis);
  const W = 1240, H = 1754, M = 40;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  await document.fonts?.ready;
  ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#050B10"; ctx.fillRect(0, 0, W, 130);
  ctx.fillStyle = "#FFD500"; ctx.fillRect(0, 130, W, 8);
  ctx.font = "900 46px 'Barlow Condensed', sans-serif"; ctx.fillStyle = "#FFD500"; ctx.fillText("NILLO", M, 66);
  const nw = ctx.measureText("NILLO ").width;
  ctx.fillStyle = "#FFFFFF"; ctx.fillText("EQUIPAMENTOS", M + nw, 66);
  ctx.font = "500 15px 'JetBrains Mono', monospace"; ctx.fillStyle = "#94A3B8"; ctx.fillText(`${BRAND.slogan} · ${BRAND.telefone}`, M, 98);
  ctx.textAlign = "right"; ctx.font = "700 20px 'Barlow Condensed', sans-serif"; ctx.fillStyle = "#FFFFFF";
  ctx.fillText("MAPA DE DESALINHAMENTO · FICHA RESUMO", W - M, 62);
  ctx.font = "500 14px 'JetBrains Mono', monospace"; ctx.fillStyle = "#94A3B8";
  ctx.fillText(`PLACA ${v.placa || "—"} · OS ${v.numero_ordem_servico || "—"} · ${fmtDay(v.data_inspecao)}`, W - M, 92);
  ctx.textAlign = "left";

  const info = [["CLIENTE", v.cliente], ["VEÍCULO", [v.marca, v.modelo, v.ano].filter(Boolean).join(" ")], ["TIPO", v.tipo_veiculo], ["EXECUTOR", v.executor]];
  info.forEach(([l, val], i) => {
    const x = M + (i % 2) * 590; const yy = 180 + Math.floor(i / 2) * 50;
    ctx.font = "500 12px 'JetBrains Mono', monospace"; ctx.fillStyle = "#64748B"; ctx.fillText(l, x, yy);
    ctx.font = "600 20px 'IBM Plex Sans', sans-serif"; ctx.fillStyle = "#050B10"; ctx.fillText(val || "—", x, yy + 24);
  });
  const rgb = final ? CLASS_STYLE[final].rgb : [100, 116, 139];
  ctx.strokeStyle = `rgb(${rgb.join(",")})`; ctx.lineWidth = 4; ctx.strokeRect(M, 290, W - 2 * M, 80);
  ctx.font = "500 12px 'JetBrains Mono', monospace"; ctx.fillStyle = "#64748B"; ctx.fillText("RESULTADO DO ALINHAMENTO", M + 20, 318);
  ctx.font = "800 30px 'Barlow Condensed', sans-serif"; ctx.fillStyle = `rgb(${rgb.join(",")})`; ctx.fillText(final || analysis.statusMsg, M + 20, 354);
  ctx.textAlign = "right"; ctx.font = "600 15px 'JetBrains Mono', monospace"; ctx.fillStyle = "#050B10";
  ctx.fillText(`MEDIDOS ${analysis.counts.medidos} · FORA ${analysis.counts.fora} · MAIOR ${analysis.maior ? `${fmtAbs(analysis.maior.valor)} (${analysis.maior.ponto})` : "—"}`, W - M - 20, 340);
  ctx.textAlign = "left";

  const top = await mapCanvas(insp, "top");
  const side = await mapCanvas(insp, "side");
  const mw = W - 2 * M;
  const th = (mw * top.height) / top.width;
  ctx.drawImage(top, M, 395, mw, th);
  const sh = (mw * side.height) / side.width;
  ctx.drawImage(side, M, 405 + th, mw, sh);
  ctx.strokeStyle = "#CBD5E1"; ctx.lineWidth = 1; ctx.strokeRect(M, 395, mw, th); ctx.strokeRect(M, 405 + th, mw, sh);

  let yy = 440 + th + sh;
  ctx.font = "700 18px 'Barlow Condensed', sans-serif"; ctx.fillStyle = "#050B10"; ctx.fillText("VETORES DE DESVIO", M, yy);
  yy += 26;
  const rows = (insp.points || []).map((p) => ({ p, c: pointCalc(p, insp.reference || {}) })).filter((x) => x.c.governing !== null);
  rows.slice(0, 16).forEach(({ p, c }, i) => {
    const x = M + (i % 2) * 590; const ry = yy + Math.floor(i / 2) * 24;
    ctx.font = "700 14px 'JetBrains Mono', monospace";
    ctx.fillStyle = c.situacao === SIT.FORA ? "#FF0000" : "#050B10";
    ctx.fillText(`${deviationLabel(p.numero, c).text} · ${c.situacao}`, x, ry);
  });
  ctx.fillStyle = "#050B10"; ctx.fillRect(0, H - 80, W, 80);
  ctx.fillStyle = "#FFD500"; ctx.fillRect(0, H - 80, W, 4);
  ctx.font = "600 13px 'JetBrains Mono', monospace"; ctx.fillStyle = "#94A3B8";
  ctx.fillText(`${BRAND.empresa} · ${BRAND.atividade}`, M, H - 46);
  ctx.fillText(`WHATSAPP ${BRAND.telefone} · ${BRAND.atendimentoRodape}`, M, H - 22);
  ctx.textAlign = "right"; ctx.font = "700 16px 'Barlow Condensed', sans-serif"; ctx.fillStyle = "#FFD500";
  ctx.fillText(BRAND.frase, W - M, H - 34);
  downloadDataUrl(canvas.toDataURL(kind === "jpg" ? "image/jpeg" : "image/png", 0.95), `${fileBase(insp).replace("Laudo", "Mapa")}.${kind}`);
}
